/**
 * portal-permission-groups.ts — Berechtigungskonzept V4.0, Bereich 6
 * (Kundenportal-Berechtigungsgruppen). Shared wire types + the fixed IGB2B taxonomy
 * grouping + the default-group template definitions.
 *
 * Consumed by apps/api (the broker-managed portal-visibility API + the default-group
 * seeding) and apps/web (the broker-cockpit portal-groups management UI). The IGB2B
 * LABELS live ONLY in the DB (migration 0121 seeds igb2b_kategorie); this file carries
 * only the CODES grouped by family so both sides build the same template category sets
 * without duplicating the German label strings.
 *
 * [Source: projects/broker-ag/berechtigungen/bereich6-portal-kategorie-build-spec.md]
 * [Source: projects/broker-ag/berechtigungen/V4.0-inbox/Berechtigungskonzept_V4.0.docx.txt Kap. 8]
 * [Source: packages/db/migrations/0121_portal_kategorie_gruppen.sql]
 */

// ── Portal-visibility mode ─────────────────────────────────────────────────────

/**
 * PORTAL_VISIBILITY_MODE — the two per-customer portal-visibility modi (Kap. 8.2).
 * 'einfach' (Default) = every portal user sees all Policen/Dokumente/Rechnungen/Schaeden.
 * 'gruppenbasiert' (Opt-in) = visibility via portal groups (category-scoped).
 * TEXT column (app-validated, rule #2 — no PG enum). customer_state.portal_visibility_mode.
 */
export const PORTAL_VISIBILITY_MODE = {
  EINFACH:        'einfach',
  GRUPPENBASIERT: 'gruppenbasiert',
} as const;

/** Union type of the portal-visibility mode values. */
export type PortalVisibilityMode =
  (typeof PORTAL_VISIBILITY_MODE)[keyof typeof PORTAL_VISIBILITY_MODE];

/** Flat readonly array of all portal-visibility mode values. */
export const PORTAL_VISIBILITY_MODE_VALUES =
  Object.values(PORTAL_VISIBILITY_MODE) as readonly PortalVisibilityMode[];

// ── IGB2B taxonomy (fixed — 59 categories in 9 families) ───────────────────────

/**
 * IGB2B_FAMILY_CODES — the branchencodes of each IGB2B Branchenfamilie (family-head code
 * -> its category codes). The fixed 59-category vocabulary, grouped. NOT extensible.
 * (The German labels live in the DB — igb2b_kategorie, seeded by migration 0121.)
 */
export const IGB2B_FAMILY_CODES = {
  '10000': ['10000', '11000', '12000', '13000', '14000'],
  '20000': ['20000', '21000', '22000', '23000', '24000', '25000', '26000', '29900'],
  '30000': ['30000', '31000', '33000', '34000', '35000', '36000', '38000', '39900'],
  '40000': ['40000', '41000', '42000', '43000'],
  '50000': ['50000', '51000', '52000', '53000', '54000', '55000', '56000', '58000', '59900'],
  '60000': ['60000', '65000', '67000', '69900'],
  '70000': ['70000', '71000', '72000', '73000', '74000', '75000', '79900'],
  '80000': ['80000', '81000', '82000', '83500', '85000', '87000', '88000', '89900'],
  '90000': ['90000', '91000', '92000', '93000', '94000', '99900'],
} as const;

/** All 59 IGB2B branchencodes (flat, for validation). */
export const IGB2B_ALL_CODES: readonly string[] = Object.values(IGB2B_FAMILY_CODES).flat();

// ── Default-group templates (seeded on switch to gruppenbasiert) ───────────────

/**
 * PortalGroupTemplateDef — one seeded editable template group (Kap. 8.4).
 * `branchencodes` = the template's default IGB2B category set. Editable/renamable/
 * deletable afterwards; the broker may also add custom groups.
 */
export interface PortalGroupTemplateDef {
  /** Stable machine key (portal_berechtigungsgruppe.template_key). */
  templateKey: string;
  /** German display name (direct umlauts; renamable). */
  name: string;
  /** 1-based display order (1..4 for the templates). */
  position: number;
  /** Default IGB2B category set. */
  branchencodes: readonly string[];
}

/** Stable template keys. */
export const PORTAL_GROUP_TEMPLATE_KEYS = {
  PERSONEN_HR:              'personen_hr',
  SACH_HAFTPFLICHT_TECHNIK: 'sach_haftpflicht_technik',
  FAHRZEUG_FLOTTE:          'fahrzeug_flotte',
  LEBEN_FINANZEN:           'leben_finanzen',
} as const;

/**
 * PORTAL_GROUP_TEMPLATES — the 4 editable template groups seeded when a customer switches
 * to gruppenbasiert (Kap. 8.4). Category counts: 14 / 36 / 5 / 4 (= 59 together; the
 * "Nicht zugeordnet" complement is therefore empty by default but recomputed dynamically).
 *   1 Personenversicherungen & HR         = Unfall(30000er) + Kranken(40000er) + Freizuegigkeit(92000) + Kollektivleben(93000)
 *   2 Sach-, Haftpflicht- & Technische V. = 20000er + 50000er + 60000er + 70000er + 80000er
 *   3 Fahrzeug & Flotte                   = 10000er
 *   4 Leben & Finanzen                    = 90000er ohne Vorsorge (90000/91000/94000/99900)
 */
export const PORTAL_GROUP_TEMPLATES: readonly PortalGroupTemplateDef[] = [
  {
    templateKey: PORTAL_GROUP_TEMPLATE_KEYS.PERSONEN_HR,
    name:        'Personenversicherungen & HR',
    position:    1,
    branchencodes: [
      ...IGB2B_FAMILY_CODES['30000'],
      ...IGB2B_FAMILY_CODES['40000'],
      '92000',
      '93000',
    ],
  },
  {
    templateKey: PORTAL_GROUP_TEMPLATE_KEYS.SACH_HAFTPFLICHT_TECHNIK,
    name:        'Sach-, Haftpflicht- & Technische Versicherungen',
    position:    2,
    branchencodes: [
      ...IGB2B_FAMILY_CODES['20000'],
      ...IGB2B_FAMILY_CODES['50000'],
      ...IGB2B_FAMILY_CODES['60000'],
      ...IGB2B_FAMILY_CODES['70000'],
      ...IGB2B_FAMILY_CODES['80000'],
    ],
  },
  {
    templateKey: PORTAL_GROUP_TEMPLATE_KEYS.FAHRZEUG_FLOTTE,
    name:        'Fahrzeug & Flotte',
    position:    3,
    branchencodes: [...IGB2B_FAMILY_CODES['10000']],
  },
  {
    templateKey: PORTAL_GROUP_TEMPLATE_KEYS.LEBEN_FINANZEN,
    name:        'Leben & Finanzen',
    position:    4,
    branchencodes: ['90000', '91000', '94000', '99900'],
  },
];

/** The non-deletable AUTO system group ("Nicht zugeordnet"). Categories + members computed. */
export const PORTAL_GROUP_SYSTEM_NAME     = 'Nicht zugeordnet' as const;
export const PORTAL_GROUP_SYSTEM_KEY      = 'nicht_zugeordnet' as const;
export const PORTAL_GROUP_SYSTEM_POSITION = 5 as const;

// ── Wire DTOs ──────────────────────────────────────────────────────────────────

/** Igb2bKategorieDto — one IGB2B category row for the category picker (GET /igb2b-kategorien). */
export interface Igb2bKategorieDto {
  branchencode:   string;
  jsonBezeichnung: string;
  deBezeichnung:  string;
  /** The family-head code ('10000'..'90000') — the web groups the picker by this. */
  familie:        string;
}

/**
 * PortalGroupMemberDto — one member of a portal group.
 * `grantId` = portal_grant.id; NULL = the primary holder (isPrimary=true). `label` = a
 * display hint (name or email; NULL when not resolvable). Membership is by grant, not by
 * login account, so a member is addressable within ONE customer.
 */
export interface PortalGroupMemberDto {
  grantId:   string | null;
  isPrimary: boolean;
  label:     string | null;
}

/**
 * PortalGroupDto — one portal-permission group (GET/POST/PATCH responses).
 * For the "Nicht zugeordnet" system group, `branchencodes` is the COMPUTED complement and
 * `members` is the COMPUTED set of all portal users (both server-computed; not stored).
 */
export interface PortalGroupDto {
  id:            string;
  name:          string;
  istSystem:     boolean;
  istTemplate:   boolean;
  templateKey:   string | null;
  position:      number;
  branchencodes: string[];
  members:       PortalGroupMemberDto[];
}

/**
 * PortalVisibilityModeDto — GET/PUT /portal-visibility-mode response.
 * `groupCount` = number of NON-SYSTEM groups (the "zero groups still sees all" indicator).
 */
export interface PortalVisibilityModeDto {
  mode:       PortalVisibilityMode;
  groupCount: number;
}

/**
 * PortalGroupsResponseDto — GET /portal-gruppen response: the mode + the full ordered group
 * list (templates, custom, then the system group last).
 */
export interface PortalGroupsResponseDto {
  mode:   PortalVisibilityMode;
  groups: PortalGroupDto[];
}
